/**
 * E2E Key Service
 *
 * Handles generation, encryption, and decryption of E2E encryption keys.
 * This service manages the identity signing key bundle and device key generation.
 *
 * SECURITY ARCHITECTURE:
 * - Signing key is encrypted with Argon2id(passphrase) and stored server-side
 * - Device keys (ECDH + KEM) are generated locally per-device
 * - Signing key is cached in memory only (never persisted locally)
 * - An optional "shared web device" keypair can be embedded in the encrypted
 *   bundle so web-app sessions can recover device keys after cache clears.
 *
 * BUNDLE FORMAT:
 * - v1 (legacy): Raw 32-byte Ed25519 signing private key
 * - v2: JSON { v:2, signingKey, webDevice: { deviceId, ecdh*, kem* } }
 * - v3: JSON { v:3, signingKey, pqSigningPrivateKey, pqSigningPublicKey, webDevice }
 *
 * @module services/e2eKeyService
 */

import {
  generateSigningKeyPair,
  generateECDHKeyPair,
  generateKEMKeyPair,
  generateMlDsa65KeyPair,
  deriveKeyFromPassword,
  encryptChaCha20Poly1305,
  decryptChaCha20Poly1305,
  computeRoutingTag,
  randomBytes,
  toBase64,
  fromBase64,
  clearBytes,
  ARGON2_DEFAULTS,
  ML_DSA_65_PUBLIC_KEY_SIZE,
  ML_DSA_65_SECRET_KEY_SIZE,
  type CryptoProfile,
} from '@adieuu/crypto';

/**
 * Input for E2E key initialization during identity creation.
 */
export interface E2EInitInput {
  /** Identity ID (hex string) */
  identityId: string;
  /** User's identity passphrase */
  passphrase: string;
  /** Human-readable device name */
  deviceName: string;
  /** Crypto profile to use (default: 'default') */
  cryptoProfile?: CryptoProfile;
  /** Whether to use a separate passphrase for the bundle */
  useSeparatePassphrase?: boolean;
  /** Separate bundle passphrase (required if useSeparatePassphrase is true) */
  bundlePassphrase?: string;
}

/**
 * Result of E2E key initialization.
 */
export interface E2EInitResult {
  /** Ed25519 signing public key (base64) */
  signingPublicKey: string;
  /** ML-DSA-65 verifying key (base64). Present on default-profile init. */
  pqSigningPublicKey?: string;
  /** Encrypted signing key bundle for server storage (v3 JSON with web device + ML-DSA-65 keys) */
  encryptedBundle: {
    encryptedBundle: string;
    salt: string;
    nonce: string;
    useSeparatePassphrase: boolean;
  };
  /** Device registration data */
  device: {
    deviceId: string;
    name: string;
    ecdhPublicKey: string;
    kemPublicKey: string;
    routingTag: string;
  };
  /** Signing private key for memory cache (will be cleared after use) */
  signingPrivateKey: Uint8Array;
  /** ML-DSA-65 signing secret for memory cache (default profile) */
  pqSigningPrivateKey?: Uint8Array;
  /** Device private keys for IndexedDB storage */
  devicePrivateKeys: {
    ecdh: Uint8Array;
    kem: Uint8Array;
  };
  /** Pre-generated shared web device keys (encrypted inside the bundle, not yet registered on server) */
  webDevice: {
    deviceId: string;
    ecdhPublicKey: string;
    kemPublicKey: string;
    privateKeys: {
      ecdh: Uint8Array;
      kem: Uint8Array;
    };
  };
}

/**
 * Result of device key generation for new device login.
 */
export interface DeviceKeysResult {
  /** Unique device identifier */
  deviceId: string;
  /** Human-readable device name */
  name: string;
  /** X25519 public key (base64) */
  ecdhPublicKey: string;
  /** ML-KEM public key (base64) */
  kemPublicKey: string;
  /** Key-fingerprint routing tag for multi-device wrapped key lookup */
  routingTag: string;
  /** Private keys for local storage */
  privateKeys: {
    ecdh: Uint8Array;
    kem: Uint8Array;
  };
}

/**
 * Decrypted web device keys from a v2 bundle.
 */
export interface DecryptedWebDevice {
  deviceId: string;
  ecdhPrivateKey: Uint8Array;
  kemPrivateKey: Uint8Array;
  ecdhPublicKey: Uint8Array;
  kemPublicKey: Uint8Array;
}

/**
 * Bundle decryption result.
 */
export interface DecryptedBundle {
  /** Ed25519 signing private key */
  signingPrivateKey: Uint8Array;
  /** ML-DSA-65 secret key (v3 bundles) */
  pqSigningPrivateKey?: Uint8Array;
  /** ML-DSA-65 public key (v3 bundles) */
  pqSigningPublicKey?: Uint8Array;
  /** Shared web device keys (present in v2/v3 bundles, absent in v1) */
  webDevice?: DecryptedWebDevice;
}

interface BundleWebDevicePlaintext {
  deviceId: string;
  ecdhPrivateKey: string;
  kemPrivateKey: string;
  ecdhPublicKey: string;
  kemPublicKey: string;
}

/**
 * V2 bundle plaintext (JSON, encrypted inside the bundle ciphertext).
 */
interface BundleV2Plaintext {
  v: 2;
  signingKey: string;
  webDevice: BundleWebDevicePlaintext;
}

/**
 * V3 bundle plaintext: v2 plus ML-DSA-65 signing keys.
 */
interface BundleV3Plaintext {
  v: 3;
  signingKey: string;
  pqSigningPrivateKey: string;
  pqSigningPublicKey: string;
  webDevice?: BundleWebDevicePlaintext;
}

type BundleJsonPlaintext = BundleV2Plaintext | BundleV3Plaintext;

/**
 * Custom error class for E2E key service errors.
 */
export class E2EKeyError extends Error {
  constructor(
    message: string,
    public readonly code: string
  ) {
    super(message);
    this.name = 'E2EKeyError';
  }
}

function webDevicePlaintext(web: DecryptedWebDevice): BundleWebDevicePlaintext {
  return {
    deviceId: web.deviceId,
    ecdhPrivateKey: toBase64(web.ecdhPrivateKey),
    kemPrivateKey: toBase64(web.kemPrivateKey),
    ecdhPublicKey: toBase64(web.ecdhPublicKey),
    kemPublicKey: toBase64(web.kemPublicKey),
  };
}

function parseWebDevice(raw: BundleWebDevicePlaintext): DecryptedWebDevice {
  return {
    deviceId: raw.deviceId,
    ecdhPrivateKey: fromBase64(raw.ecdhPrivateKey),
    kemPrivateKey: fromBase64(raw.kemPrivateKey),
    ecdhPublicKey: fromBase64(raw.ecdhPublicKey),
    kemPublicKey: fromBase64(raw.kemPublicKey),
  };
}

async function encryptPlaintextWithPassphrase(
  plaintextBytes: Uint8Array,
  passphrase: string,
  derivationErrorCode: 'KEY_DERIVATION_FAILED'
): Promise<{ encryptedBundle: string; salt: string; nonce: string }> {
  const salt = randomBytes(16);
  let derivedKey: Uint8Array;

  try {
    derivedKey = await deriveKeyFromPassword({
      password: passphrase,
      salt,
      memoryCost: ARGON2_DEFAULTS.memoryCost,
      timeCost: ARGON2_DEFAULTS.timeCost,
      parallelism: ARGON2_DEFAULTS.parallelism,
      outputLength: 32,
    });
  } catch {
    throw new E2EKeyError(
      'Failed to derive encryption key from passphrase',
      derivationErrorCode
    );
  }

  const { ciphertext, nonce } = encryptChaCha20Poly1305(derivedKey, plaintextBytes);
  clearBytes(derivedKey);
  clearBytes(plaintextBytes);

  return {
    encryptedBundle: toBase64(ciphertext),
    salt: toBase64(salt),
    nonce: toBase64(nonce),
  };
}

/**
 * Generates all keys needed for E2E encryption initialization.
 *
 * This is called during identity creation to:
 * 1. Generate Ed25519 signing key pair (identity-level)
 * 2. Generate ML-DSA-65 signing key pair (identity-level, default profile)
 * 3. Generate X25519 ECDH key pair (device-level)
 * 4. Generate ML-KEM key pair (device-level, post-quantum)
 * 5. Generate shared web device ECDH+KEM key pair
 * 6. Build v3 bundle JSON (signing keys + web device keys) and encrypt
 *
 * The web device keys are pre-generated and stored in the encrypted bundle
 * but NOT registered on the server. Registration happens lazily when the
 * user opts into shared web mode on their first web login.
 *
 * @param input - Initialization parameters
 * @returns Generated keys and encrypted bundle
 * @throws E2EKeyError if key generation or encryption fails
 */
export async function generateE2EKeys(input: E2EInitInput): Promise<E2EInitResult> {
  const profile = input.cryptoProfile ?? 'default';

  // Validate separate passphrase input
  if (input.useSeparatePassphrase && !input.bundlePassphrase) {
    throw new E2EKeyError(
      'Bundle passphrase required when using separate passphrase',
      'MISSING_BUNDLE_PASSPHRASE'
    );
  }

  // 1. Generate signing key pair (Ed25519)
  const signingKeyPair = generateSigningKeyPair();
  const pqSigningKeyPair = profile === 'default' ? generateMlDsa65KeyPair() : undefined;

  // 2. Generate local device keys
  const ecdhKeyPair = generateECDHKeyPair();
  const kemKeyPair = generateKEMKeyPair(profile);

  // 3. Generate shared web device keys
  const webEcdhKeyPair = generateECDHKeyPair();
  const webKemKeyPair = generateKEMKeyPair(profile);
  const webDeviceId = crypto.randomUUID();

  // 4. Build bundle JSON and encrypt with Argon2id
  const bundlePassphrase = input.useSeparatePassphrase
    ? input.bundlePassphrase!
    : input.passphrase;

  const webDevice: BundleWebDevicePlaintext = {
    deviceId: webDeviceId,
    ecdhPrivateKey: toBase64(webEcdhKeyPair.privateKey),
    kemPrivateKey: toBase64(webKemKeyPair.privateKey),
    ecdhPublicKey: toBase64(webEcdhKeyPair.publicKey),
    kemPublicKey: toBase64(webKemKeyPair.publicKey),
  };

  const bundlePlaintext: BundleJsonPlaintext = pqSigningKeyPair
    ? {
        v: 3,
        signingKey: toBase64(signingKeyPair.privateKey),
        pqSigningPrivateKey: toBase64(pqSigningKeyPair.privateKey),
        pqSigningPublicKey: toBase64(pqSigningKeyPair.publicKey),
        webDevice,
      }
    : {
        v: 2,
        signingKey: toBase64(signingKeyPair.privateKey),
        webDevice,
      };

  const plaintextBytes = new TextEncoder().encode(JSON.stringify(bundlePlaintext));

  let encrypted: { encryptedBundle: string; salt: string; nonce: string };
  try {
    encrypted = await encryptPlaintextWithPassphrase(
      plaintextBytes,
      bundlePassphrase,
      'KEY_DERIVATION_FAILED'
    );
  } catch (err) {
    if (err instanceof E2EKeyError) throw err;
    throw new E2EKeyError(
      'Failed to derive encryption key from passphrase',
      'KEY_DERIVATION_FAILED'
    );
  }

  // 5. Generate local device ID
  const deviceId = crypto.randomUUID();

  return {
    signingPublicKey: toBase64(signingKeyPair.publicKey),
    pqSigningPublicKey: pqSigningKeyPair
      ? toBase64(pqSigningKeyPair.publicKey)
      : undefined,
    encryptedBundle: {
      ...encrypted,
      useSeparatePassphrase: input.useSeparatePassphrase ?? false,
    },
    device: {
      deviceId,
      name: input.deviceName,
      ecdhPublicKey: toBase64(ecdhKeyPair.publicKey),
      kemPublicKey: toBase64(kemKeyPair.publicKey),
      routingTag: computeRoutingTag(ecdhKeyPair.publicKey, kemKeyPair.publicKey),
    },
    signingPrivateKey: signingKeyPair.privateKey,
    pqSigningPrivateKey: pqSigningKeyPair?.privateKey,
    devicePrivateKeys: {
      ecdh: ecdhKeyPair.privateKey,
      kem: kemKeyPair.privateKey,
    },
    webDevice: {
      deviceId: webDeviceId,
      ecdhPublicKey: toBase64(webEcdhKeyPair.publicKey),
      kemPublicKey: toBase64(webKemKeyPair.publicKey),
      privateKeys: {
        ecdh: webEcdhKeyPair.privateKey,
        kem: webKemKeyPair.privateKey,
      },
    },
  };
}

/**
 * Re-encrypt a decrypted bundle with a new passphrase.
 *
 * Used during passphrase change and PQ-signing upgrades: the caller decrypts
 * with the old passphrase (via `decryptKeyBundle`), then re-encrypts here.
 * ML-DSA-65 keys produce v3 even when the bundle has no shared web device.
 *
 * @param decrypted - Decrypted bundle from `decryptKeyBundle`
 * @param newPassphrase - Passphrase to encrypt with
 * @returns Encrypted bundle ready for server upload
 * @throws E2EKeyError if encryption fails
 */
export async function encryptKeyBundle(
  decrypted: DecryptedBundle,
  newPassphrase: string,
): Promise<{ encryptedBundle: string; salt: string; nonce: string }> {
  let plaintextBytes: Uint8Array;

  if (decrypted.pqSigningPrivateKey && decrypted.pqSigningPublicKey) {
    const bundlePlaintext: BundleV3Plaintext = {
      v: 3,
      signingKey: toBase64(decrypted.signingPrivateKey),
      pqSigningPrivateKey: toBase64(decrypted.pqSigningPrivateKey),
      pqSigningPublicKey: toBase64(decrypted.pqSigningPublicKey),
      ...(decrypted.webDevice
        ? { webDevice: webDevicePlaintext(decrypted.webDevice) }
        : {}),
    };
    plaintextBytes = new TextEncoder().encode(JSON.stringify(bundlePlaintext));
  } else if (decrypted.webDevice) {
    const bundlePlaintext: BundleV2Plaintext = {
      v: 2,
      signingKey: toBase64(decrypted.signingPrivateKey),
      webDevice: webDevicePlaintext(decrypted.webDevice),
    };
    plaintextBytes = new TextEncoder().encode(JSON.stringify(bundlePlaintext));
  } else {
    plaintextBytes = new Uint8Array(decrypted.signingPrivateKey);
  }

  return encryptPlaintextWithPassphrase(
    plaintextBytes,
    newPassphrase,
    'KEY_DERIVATION_FAILED',
  );
}

/**
 * Attach ML-DSA-65 keys when a decrypted bundle still lacks them (v1/v2).
 * Callers must persist the result via {@link encryptKeyBundle} before using
 * the new secret to sign; unpublished PQ keys produce unverifiable hybrids.
 */
export function withPqSigningKeys(decrypted: DecryptedBundle): {
  bundle: DecryptedBundle;
  generated: boolean;
} {
  if (decrypted.pqSigningPrivateKey && decrypted.pqSigningPublicKey) {
    return { bundle: decrypted, generated: false };
  }
  const pq = generateMlDsa65KeyPair();
  return {
    bundle: {
      ...decrypted,
      pqSigningPrivateKey: pq.privateKey,
      pqSigningPublicKey: pq.publicKey,
    },
    generated: true,
  };
}

/**
 * Generates device keys for a new device login.
 *
 * Called when logging into an identity from a device that doesn't have
 * existing device keys in IndexedDB.
 *
 * @param deviceName - Human-readable device name
 * @param cryptoProfile - Crypto profile to use
 * @returns Generated device keys
 */
export function generateDeviceKeys(
  deviceName: string,
  cryptoProfile: CryptoProfile = 'default'
): DeviceKeysResult {
  const ecdhKeyPair = generateECDHKeyPair();
  const kemKeyPair = generateKEMKeyPair(cryptoProfile);
  const deviceId = crypto.randomUUID();

  const ecdhPub = toBase64(ecdhKeyPair.publicKey);
  const kemPub = toBase64(kemKeyPair.publicKey);

  return {
    deviceId,
    name: deviceName,
    ecdhPublicKey: ecdhPub,
    kemPublicKey: kemPub,
    routingTag: computeRoutingTag(ecdhKeyPair.publicKey, kemKeyPair.publicKey),
    privateKeys: {
      ecdh: ecdhKeyPair.privateKey,
      kem: kemKeyPair.privateKey,
    },
  };
}

/**
 * Decrypts a key bundle retrieved from the server.
 *
 * Uses Argon2id to derive the decryption key from the passphrase,
 * then decrypts the bundle using ChaCha20-Poly1305.
 *
 * Supports three formats:
 * - v1 (legacy): Raw 32-byte Ed25519 signing private key
 * - v2: JSON with signing key + optional shared web device keys
 * - v3: v2 plus ML-DSA-65 signing keys
 *
 * @param encryptedBundle - Encrypted bundle data from server
 * @param passphrase - Passphrase (identity or separate bundle passphrase)
 * @returns Decrypted bundle contents
 * @throws E2EKeyError if decryption fails (wrong passphrase or corrupted data)
 */
export async function decryptKeyBundle(
  encryptedBundle: {
    encryptedBundle: string;
    salt: string;
    nonce: string;
  },
  passphrase: string
): Promise<DecryptedBundle> {
  let salt: Uint8Array;
  let nonce: Uint8Array;
  let ciphertext: Uint8Array;

  try {
    salt = fromBase64(encryptedBundle.salt);
    nonce = fromBase64(encryptedBundle.nonce);
    ciphertext = fromBase64(encryptedBundle.encryptedBundle);
  } catch {
    throw new E2EKeyError('Invalid bundle data format', 'INVALID_BUNDLE_DATA');
  }

  if (salt.length < 8) {
    throw new E2EKeyError('Invalid salt size', 'INVALID_SALT_SIZE');
  }
  if (nonce.length !== 12) {
    throw new E2EKeyError('Invalid nonce size', 'INVALID_NONCE_SIZE');
  }

  let derivedKey: Uint8Array;
  try {
    derivedKey = await deriveKeyFromPassword({
      password: passphrase,
      salt,
      memoryCost: ARGON2_DEFAULTS.memoryCost,
      timeCost: ARGON2_DEFAULTS.timeCost,
      parallelism: ARGON2_DEFAULTS.parallelism,
      outputLength: 32,
    });
  } catch {
    throw new E2EKeyError(
      'Failed to derive decryption key',
      'KEY_DERIVATION_FAILED'
    );
  }

  let plaintext: Uint8Array;
  try {
    plaintext = decryptChaCha20Poly1305(derivedKey, ciphertext, nonce);
  } catch {
    throw new E2EKeyError(
      'Failed to decrypt bundle. Check your passphrase.',
      'DECRYPTION_FAILED'
    );
  } finally {
    clearBytes(derivedKey);
  }

  // v1 format: raw 32-byte Ed25519 private key
  if (plaintext.length === 32) {
    return { signingPrivateKey: plaintext };
  }

  // v2/v3 format: JSON
  let parsed: { v?: number; signingKey?: string; pqSigningPrivateKey?: string; pqSigningPublicKey?: string; webDevice?: BundleWebDevicePlaintext };
  try {
    const json = new TextDecoder().decode(plaintext);
    parsed = JSON.parse(json) as typeof parsed;
  } catch {
    clearBytes(plaintext);
    throw new E2EKeyError('Invalid bundle plaintext format', 'INVALID_BUNDLE_FORMAT');
  }

  clearBytes(plaintext);

  if ((parsed.v !== 2 && parsed.v !== 3) || !parsed.signingKey) {
    throw new E2EKeyError('Unsupported bundle version', 'UNSUPPORTED_BUNDLE_VERSION');
  }

  const signingPrivateKey = fromBase64(parsed.signingKey);
  if (signingPrivateKey.length !== 32) {
    clearBytes(signingPrivateKey);
    throw new E2EKeyError('Decrypted signing key has invalid size', 'INVALID_KEY_SIZE');
  }

  let pqSigningPrivateKey: Uint8Array | undefined;
  let pqSigningPublicKey: Uint8Array | undefined;
  if (parsed.v === 3) {
    if (!parsed.pqSigningPrivateKey || !parsed.pqSigningPublicKey) {
      clearBytes(signingPrivateKey);
      throw new E2EKeyError('v3 bundle missing ML-DSA-65 keys', 'INVALID_BUNDLE_FORMAT');
    }
    pqSigningPrivateKey = fromBase64(parsed.pqSigningPrivateKey);
    pqSigningPublicKey = fromBase64(parsed.pqSigningPublicKey);
    if (
      pqSigningPrivateKey.length !== ML_DSA_65_SECRET_KEY_SIZE ||
      pqSigningPublicKey.length !== ML_DSA_65_PUBLIC_KEY_SIZE
    ) {
      clearBytes(signingPrivateKey);
      clearBytes(pqSigningPrivateKey);
      clearBytes(pqSigningPublicKey);
      throw new E2EKeyError('Decrypted ML-DSA-65 key has invalid size', 'INVALID_KEY_SIZE');
    }
  }

  let webDevice: DecryptedWebDevice | undefined;
  if (parsed.webDevice) {
    webDevice = parseWebDevice(parsed.webDevice);
  }

  return { signingPrivateKey, pqSigningPrivateKey, pqSigningPublicKey, webDevice };
}

/**
 * Gets a default device name based on the platform.
 *
 * Attempts to detect the device type from the user agent.
 *
 * @returns Human-readable device name
 */
export function getDefaultDeviceName(): string {
  if (typeof navigator === 'undefined') {
    return 'Unknown Device';
  }

  const ua = navigator.userAgent.toLowerCase();

  // Check for mobile platforms
  if (/iphone|ipad|ipod/.test(ua)) {
    return /ipad/.test(ua) ? 'iPad' : 'iPhone';
  }
  if (/android/.test(ua)) {
    return /mobile/.test(ua) ? 'Android Phone' : 'Android Tablet';
  }

  // Check for desktop platforms
  if (/macintosh|mac os x/.test(ua)) {
    return 'Mac';
  }
  if (/windows/.test(ua)) {
    return 'Windows PC';
  }
  if (/linux/.test(ua)) {
    return 'Linux';
  }

  // Fallback based on context
  if (/electron/.test(ua)) {
    return 'Desktop App';
  }

  return 'Web Browser';
}
